import React, { useState } from "react"
import { useParams } from "react-router-dom"
import pluralize from "pluralize"
import { Flex, HStack, Text } from "@chakra-ui/react"
import Link from "components/elements/Link"
import { useKeyPress } from "ahooks"
import useGuestsStore from "services/useGuestsStore"
import { useSegmentSizes } from "queries/events"
import { useShallow } from "zustand/shallow"
import {
  EventCounts, EventGroup, Segment, Table,
} from "sharedTypes"
import usePermissions from "services/permissions"
import useFeatureFlag from "services/featureFlags"
import ActionButton from "./ActionButton"
import SelectAllLink from "../SelectAllLink"
import Action, { Actions } from "./Action"
import { selectedFilterCount } from "../ScrollView/Filters"

type Props = {
  eventGroups: EventGroup[]
  segments: Segment[]
  counts: EventCounts
  tables: Table[]
}

const MultipleSelectActions = ({
  eventGroups, segments, counts, tables,
}: Props) => {
  const [action, setAction] = useState<Actions | null>(null)
  const { canViewBadges } = usePermissions()
  const { badgeBuilder: badgeBuilderEnabled } = useFeatureFlag()
  const {
    selectedGuestIds,
    selectAll,
    deselectAll,
    allSelected,
    groupId,
    segmentId,
  } = useGuestsStore(
    useShallow((state) => ({
      selectedGuestIds: state.selectedGuestIds,
      selectAll: state.selectAll,
      deselectAll: state.deselectAll,
      allSelected: state.allSelected,
      groupId: state.groupId,
      segmentId: state.segmentId,
    })),
  )

  const { eventId } = useParams<{ eventId: string }>()
  const segmentSizes = useSegmentSizes(eventId)

  let count
  const handleCancel = () => {
    deselectAll()
  }

  const handleSelectAll = () => {
    selectAll()
  }

  if (allSelected) {
    count = selectedFilterCount(eventGroups, counts, segments, segmentId, groupId, segmentSizes)
  } else {
    count = selectedGuestIds.length
  }

  useKeyPress("esc", () => handleCancel())

  return (
    <>
      <Flex direction="column" justify="center" align="center" h="full">
        <Text fontSize="2xl" data-testid="guest-number-counter-text">{count === null ? "All matching guests selected" : `${count} ${pluralize("guest", count)} selected`}</Text>
        <HStack fontSize="lg" textDecoration="underline" spacing={4}>
          {!allSelected && <SelectAllLink onClick={handleSelectAll} />}
          <Link onClick={handleCancel}>cancel</Link>
        </HStack>
        <Flex mt={16}>
          <ActionButton
            icon="circle"
            text="Change Status"
            onClick={() => setAction("change-status")}
          />
          <ActionButton
            icon="tag"
            text="Tag Guest List"
            onClick={() => setAction("tag-guest-list")}
          />
          <ActionButton
            icon="invite-seat"
            text="Assign Seat"
            onClick={() => setAction("assign-seat")}
          />
          <ActionButton
            icon="email-only-large"
            text="Send Email"
            onClick={() => setAction("send-email")}
          />
          <ActionButton
            icon="smart-reminder"
            text="Send Smart Reminder"
            onClick={() => setAction("send-smart-reminder")}
          />
          {canViewBadges && badgeBuilderEnabled && (
            <ActionButton
              icon="badges"
              text="Print Badges"
              onClick={() => setAction("print-badge")}
            />
          )}
          <ActionButton
            icon="times"
            text="Remove From Event"
            onClick={() => setAction("remove-from-event")}
            color="red.700"
          />
        </Flex>
      </Flex>
      <Action
        action={action}
        onClose={() => setAction(null)}
        selectedCount={count}
        tables={tables}
      />
    </>
  )
}

export default MultipleSelectActions
